import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import {
  Bell,
  ChevronDown,
  Code2,
  HelpCircle,
  Home,
  Landmark,
  Link2,
  ListOrdered,
  LogOut,
  Menu,
  Search,
  Settings,
  ShieldCheck,
  Users,
  X,
  CreditCard,
  ArrowLeftRight,
  Building2,
  Globe,
  Sparkles,
  Headset,
  Activity,
  CalendarDays,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useDashboard } from "@/features/dashboard/dashboard-context";
import { fmtDate } from "@/types/dashboard";
import { Logo } from "./bits";
import { useAuth } from "@/features/auth/auth-context";
import { useI18n, LanguageSwitcher, LanguageFlag } from "@/i18n/i18n-context";
import { publicApi } from "@/services/api-client";

type Item = { href: string; label: string; badge?: boolean };
type Group = {
  label: string;
  icon: typeof Home;
  href?: string;
  items?: Item[];
  businessOnly?: boolean;
};

function useNav(locked: boolean, t: (k: string) => string): Group[] {
  return [
    { label: t("nav.home"), icon: Home, href: "/" },
    {
      label: t("nav.finances"),
      icon: Landmark,
      items: [
        { href: "/wallets", label: t("nav.wallets"), badge: locked },
        { href: "/services", label: t("nav.countriesAndCurrencies") },
        { href: "/transactions?op=DEPOSIT", label: t("nav.deposits") },
        { href: "/transactions?op=WITHDRAWAL", label: t("nav.withdrawals") },
        { href: "/finance", label: t("nav.conversionsAndReservations") },
      ],
    },
    { label: t("nav.virtualCards"), icon: CreditCard, href: "/cards" },
    { label: t("nav.accounts"), icon: Building2, href: "/accounts" },
    {
      label: t("nav.transactions"),
      icon: ListOrdered,
      items: [
        { href: "/transactions", label: t("nav.history") },
        { href: "/transactions?status=PENDING", label: t("nav.pending") },
      ],
    },
    {
      label: t("nav.paymentLinks"),
      icon: Link2,
      href: "/liens-de-paiement",
    },
  ];
}

function NavBody({ onNav }: { onNav?: () => void }) {
  const [loc] = useLocation();
  const { locked, isBusiness } = useDashboard();
  const { t } = useI18n();
  const { identity } = useAuth();
  const [assistantEnabled, setAssistantEnabled] = useState(false);
  useEffect(() => {
    if (!isBusiness) return;
    let active = true;
    const refresh = () =>
      void publicApi<{ enabled: boolean }>("/assistant")
        .then((response) => {
          if (active) setAssistantEnabled(response.data.enabled);
        })
        .catch(() => {
          if (active) setAssistantEnabled(false);
        });
    refresh();
    const timer = window.setInterval(refresh, 20_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [isBusiness, identity?.userId, identity?.accountId]);
  const [open, setOpen] = useState<Record<string, boolean>>({
    Finances: true,
    Transactions: true,
    Développeurs: true,
    Developers: true,
  });
  const linkCls = (active: boolean) =>
    cn(
      "group flex items-center gap-2.5 rounded-lg px-3 py-2 text-[13.5px] font-medium transition-colors",
      active
        ? "bg-sidebar-accent text-sidebar-accent-foreground"
        : "text-sidebar-foreground hover:bg-sidebar-accent/60 hover:text-foreground",
    );
  const isActive = (h: string) => {
    const p = h.split("?")[0];
    return p === "/" ? loc === "/" : loc.startsWith(p) && !h.includes("?");
  };
  const bottom: Group[] = [
    { label: t("nav.team"), icon: Users, href: "/equipe", businessOnly: true },
    {
      label: t("nav.developers"),
      icon: Code2,
      businessOnly: true,
      items: [
        { href: "/developpeurs", label: t("nav.appsAndWebhooks") },
        {
          href: "https://docs.bizconnectpay.com",
          label: t("nav.apiDocumentation"),
        },
      ],
    },
    { label: t("nav.verification"), icon: ShieldCheck, href: "/verification" },
    { label: t("nav.settings"), icon: Settings, href: "/settings" },
    { label: t("nav.notifications"), icon: Bell, href: "/notifications" },
    // {
    //   label: t("nav.support"),
    //   icon: Headset,
    //   href: "/support",
    //   businessOnly: true,
    // },
    {
      label: t("nav.assistant"),
      icon: Sparkles,
      href: "/assistant",
      businessOnly: true,
    },
  ];
  const Warn = () => (
    <span className="ml-auto flex h-4 w-4 items-center justify-center rounded-full border-[1.5px] border-amber-500 text-[10px] font-bold text-amber-600">
      !
    </span>
  );
  const render = (g: Group) => {
    if (g.businessOnly && !isBusiness) return null;
    if (g.href === "/assistant" && !assistantEnabled) return null;

    return g.items ? (
      <div key={g.label}>
        <button
          data-testid={`nav-group-${g.label}`}
          aria-expanded={open[g.label]}
          onClick={() => setOpen((o) => ({ ...o, [g.label]: !o[g.label] }))}
          className={linkCls(false) + " w-full"}
        >
          <g.icon className="h-4 w-4" />
          {g.label}
          <ChevronDown
            className={cn(
              "ml-auto h-4 w-4 transition-transform",
              !open[g.label] && "-rotate-90",
            )}
          />
        </button>
        {open[g.label] && (
          <div className="ml-[22px] mt-0.5 space-y-0.5 border-l pl-3">
            {g.items.map((it) =>
              /^https:\/\//.test(it.href) ? (
                <a
                  key={it.href}
                  href={it.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onNav}
                  data-testid={`nav-${it.label}`}
                  className="flex items-center rounded-md px-2.5 py-1.5 text-[13px] text-muted-foreground hover:text-foreground"
                >
                  {it.label}{" "}
                  <span className="ml-auto" aria-label="nouvel onglet">
                    ↗
                  </span>
                </a>
              ) : (
                <Link
                  key={it.href}
                  href={it.href}
                  onClick={onNav}
                  data-testid={`nav-${it.label}`}
                  className={cn(
                    "flex items-center rounded-md px-2.5 py-1.5 text-[13px] transition-colors",
                    isActive(it.href)
                      ? "font-semibold text-primary"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {it.label}
                  {it.badge && <Warn />}
                </Link>
              ),
            )}
          </div>
        )}
      </div>
    ) : (
      <Link
        key={g.label}
        href={g.href!}
        onClick={onNav}
        data-testid={`nav-${g.label}`}
        className={linkCls(isActive(g.href!))}
      >
        <g.icon className="h-4 w-4" />
        {g.label}
      </Link>
    );
  };
  return (
    <nav
      aria-label="Navigation principale"
      className="flex h-full flex-col justify-between gap-6 p-3"
    >
      <div className="space-y-1">{useNav(locked, t).map(render)}</div>
      <div className="space-y-1">{bottom.map(render)}</div>
    </nav>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const [mobile, setMobile] = useState(false);
  const [q, setQ] = useState("");
  const [statusTab, setStatusTab] = useState<"INCIDENT" | "MAINTENANCE">(
    "INCIDENT",
  );
  const [loc, nav] = useLocation();
  const d = useDashboard();
  const { logout } = useAuth();
  const { t, lang, setLanguage } = useI18n();
  const unread = d.notices.filter((n) => !n.read).length;
  const activeIncidents = d.statusEvents.filter(
    (event) => event.type === "INCIDENT" && !event.resolved_at,
  ).length;
  const visibleStatusEvents = d.statusEvents.filter(
    (event) => event.type === statusTab,
  );
  const initials = d.profile.name
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="min-h-[100dvh] bg-background">
      <header className="topbar sticky top-0 z-40 flex h-14 items-center gap-3 px-3 text-white shadow-[0_4px_20px_-8px_hsl(222_75%_10%/0.55)] sm:px-5">
        <button
          className="rounded-md p-1.5 hover:bg-white/10 lg:hidden"
          aria-label="Ouvrir le menu"
          data-testid="button-mobile-menu"
          onClick={() => setMobile(true)}
        >
          <Menu className="h-5 w-5" />
        </button>
        <Link href="/" data-testid="link-home-logo" className="shrink-0">
          <Logo light />
        </Link>
        <form
          className="mx-auto hidden w-full max-w-md md:block"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            nav(`/transactions?q=${encodeURIComponent(q)}`);
          }}
        >
          <label className="flex h-9 items-center gap-2 rounded-lg bg-white px-3 text-foreground ring-white/30 transition focus-within:ring-4">
            <Search className="h-4 w-4 text-muted-foreground" />
            <span className="sr-only">Rechercher des transactions</span>
            <input
              data-testid="input-global-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("topbar.searchPlaceholder")}
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </label>
        </form>
        <div className="ml-auto flex items-center gap-1.5 md:ml-0">
          <Popover>
            <PopoverTrigger
              data-testid="button-notifications"
              className="relative flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-semibold hover:bg-white/10"
            >
              {unread > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#00E5A8] px-1 text-[11px] font-bold text-[#0B1F4B]">
                  {unread}
                </span>
              )}
              <Bell className="h-4 w-4 sm:hidden" />
              <span className="hidden sm:inline">
                {t("topbar.notifications")}
              </span>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <p className="text-sm font-bold">{t("topbar.notifications")}</p>
                <button
                  data-testid="button-mark-all-read"
                  onClick={() => nav("/notifications")}
                  className="text-xs font-semibold text-primary hover:underline"
                >
                  {t("topbar.markAllRead")}
                </button>
              </div>
              <ul className="max-h-80 overflow-auto">
                {d.notices.slice(0, 8).map((n) => (
                  <li key={n.id}>
                    <button
                      onClick={() =>
                        void d.markRead(n.id).catch((error: unknown) =>
                          toast({
                            title: t("topbar.notifications"),
                            description:
                              error instanceof Error
                                ? error.message
                                : "Lecture impossible",
                            variant: "destructive",
                          }),
                        )
                      }
                      className={cn(
                        "w-full px-4 py-3 text-left transition hover:bg-accent/60",
                        !n.read && "bg-accent/30",
                      )}
                    >
                      <p className="flex items-center gap-2 text-[13px] font-semibold">
                        {!n.read && (
                          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                        )}
                        <span className="min-w-0 flex-1 truncate">
                          {n.title}
                        </span>
                        {n.status && (
                          <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
                            {n.status}
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                        {n.body}
                      </p>
                      <p className="mt-1 text-[10px] text-muted-foreground">
                        {fmtDate(n.createdAt)}
                      </p>
                    </button>
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger
              data-testid="button-service-status"
              aria-label={t("topbar.serviceStatus")}
              className="relative flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-semibold hover:bg-white/10"
            >
              <Activity className="h-4 w-4" />
              {activeIncidents > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {activeIncidents}
                </span>
              )}
              <span className="hidden lg:inline">
                {t("topbar.serviceStatus")}
              </span>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-[min(26rem,calc(100vw-1rem))] p-0"
            >
              <div className="border-b px-4 py-3">
                <p className="text-sm font-bold">{t("topbar.serviceStatus")}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {t("topbar.statusIntro")}
                </p>
              </div>
              <div className="grid grid-cols-2 border-b px-3">
                {(
                  [
                    ["INCIDENT", t("topbar.incidents"), Activity],
                    ["MAINTENANCE", t("topbar.maintenance"), CalendarDays],
                  ] as const
                ).map(([type, label, Icon]) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setStatusTab(type)}
                    className={cn(
                      "flex items-center justify-center gap-2 border-b-2 px-2 py-3 text-sm font-semibold",
                      statusTab === type
                        ? "border-primary text-primary"
                        : "border-transparent text-muted-foreground",
                    )}
                  >
                    <Icon className="h-4 w-4" /> {label}
                    <span className="rounded-full bg-muted px-1.5 text-[10px]">
                      {
                        d.statusEvents.filter((event) => event.type === type)
                          .length
                      }
                    </span>
                  </button>
                ))}
              </div>
              <ul className="max-h-80 divide-y overflow-auto">
                {visibleStatusEvents.slice(0, 8).map((event) => (
                  <li key={event.id} className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <a
                        className="text-[13px] font-semibold hover:text-primary hover:underline"
                        href={`https://status.bizconnectpay.com/incidents/${event.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {event.title}
                      </a>
                      <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                        {event.status.replaceAll("_", " ")}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {[
                        event.country_name,
                        event.provider,
                        event.payment_method,
                        event.service_name,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                      {event.body}
                    </p>
                    <p className="mt-1 text-[10px] text-muted-foreground">
                      {fmtDate(event.updated_at)}
                    </p>
                  </li>
                ))}
                {visibleStatusEvents.length === 0 && (
                  <li className="px-4 py-7 text-center text-sm text-muted-foreground">
                    {statusTab === "INCIDENT"
                      ? t("topbar.noIncidents")
                      : t("topbar.noMaintenance")}
                  </li>
                )}
              </ul>
              <a
                href="https://status.bizconnectpay.com/history"
                target="_blank"
                rel="noreferrer"
                className="block border-t px-4 py-3 text-center text-xs font-semibold text-primary hover:bg-accent/50"
              >
                {t("topbar.statusHistory")}
              </a>
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger
              data-testid="button-help"
              className="hidden items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold hover:bg-white/10 sm:flex"
            >
              <HelpCircle className="h-4 w-4" />
              {t("topbar.help")}
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="w-72 text-xs leading-relaxed"
            >
              <p className="mb-1 text-sm font-bold">{t("topbar.helpTitle")}</p>
              {t("topbar.helpDesc")}
            </PopoverContent>
          </Popover>
          <Link
            href="/assistant"
            data-testid="button-assistant"
            aria-current={loc.startsWith("/assistant") ? "page" : undefined}
            className={cn(
              "hidden items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-semibold hover:bg-white/10 sm:flex",
              loc.startsWith("/assistant") && "bg-white/10",
            )}
          >
            <Sparkles className="h-4 w-4" />
            {t("nav.assistant")}
          </Link>

          {/* Language Switcher with Flag Icons */}
          <LanguageSwitcher variant="topbar" />

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="button-account-menu"
              aria-label="Menu du compte"
              className="ml-1 flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#00D4FF] to-[#0066FF] text-xs font-bold text-white ring-2 ring-white/30 transition hover:ring-white/60"
            >
              {initials}
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuLabel>
                <p className="text-sm font-semibold">
                  {d.profile.name || t("topbar.accountDefault")}
                </p>
                <p className="text-xs font-normal text-muted-foreground">
                  {d.profile.email}
                </p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => nav("/settings")}>
                <Settings className="mr-2 h-4 w-4" />
                {t("topbar.settings")}
              </DropdownMenuItem>
              {/* <DropdownMenuItem onClick={() => nav("/support")}>
                <Headset className="mr-2 h-4 w-4" />
                {t("nav.support")}
              </DropdownMenuItem> */}
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
                <Globe className="h-3.5 w-3.5" />
                <span>{t("topbar.language")}</span>
              </div>
              <div className="grid grid-cols-2 gap-1 px-2 pb-1.5 pt-0.5">
                <button
                  type="button"
                  data-testid="dropdown-lang-fr"
                  onClick={() => setLanguage("fr")}
                  className={cn(
                    "flex items-center justify-center gap-1.5 rounded-md py-1.5 px-2 text-xs transition-colors border",
                    lang === "fr"
                      ? "border-primary bg-primary/10 text-primary font-bold shadow-xs"
                      : "border-slate-200 text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  <LanguageFlag code="fr" className="text-sm" />
                  <span>Français</span>
                </button>
                <button
                  type="button"
                  data-testid="dropdown-lang-en"
                  onClick={() => setLanguage("en")}
                  className={cn(
                    "flex items-center justify-center gap-1.5 rounded-md py-1.5 px-2 text-xs transition-colors border",
                    lang === "en"
                      ? "border-primary bg-primary/10 text-primary font-bold shadow-xs"
                      : "border-slate-200 text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  <LanguageFlag code="en" className="text-sm" />
                  <span>English</span>
                </button>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => void logout()}
                className="text-destructive"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t("topbar.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <aside className="fixed bottom-0 left-0 top-14 hidden w-60 overflow-y-auto border-r bg-sidebar lg:block">
        <NavBody />
      </aside>

      {mobile && (
        <div
          className="fixed inset-0 z-50 lg:hidden"
          role="dialog"
          aria-modal
          aria-label="Menu"
        >
          <div
            className="absolute inset-0 bg-[#0B1F4B]/55 backdrop-blur-[2px]"
            onClick={() => setMobile(false)}
          />
          <div className="rise absolute inset-y-0 left-0 flex w-72 flex-col bg-sidebar shadow-2xl">
            <div className="flex h-14 items-center justify-between border-b px-4">
              <Logo />
              <button
                aria-label="Fermer le menu"
                data-testid="button-close-menu"
                onClick={() => setMobile(false)}
                className="rounded-md p-1.5 hover:bg-accent"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <NavBody onNav={() => setMobile(false)} />
            </div>
          </div>
        </div>
      )}

      <main className="lg:pl-60">
        <div className="mx-auto max-w-[1280px] px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
          {children}
        </div>
      </main>
    </div>
  );
}
